


import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import NoneAddedPopup from '../NoneAddedPopup';
import { validateRules } from '../../../../../reducers/JsonRuleEditor/RulesActions';

export default function Validate() {
    const dispatch = useDispatch();
    const currentRule = useSelector(state => state.JsonRulesEditorReducer.currentRule);
    const facts = currentRule?.facts || [];
    const validationResults = useSelector(state => state.JsonRulesEditorReducer.validationResults);
    const validationError = useSelector(state => state.JsonRulesEditorReducer.error);
    
    // Store temporary fact values for testing
    const [factValues, setFactValues] = useState({});
    const [isValidating, setIsValidating] = useState(false);

    const handleFactValueChange = (factName, value) => {
        setFactValues(prev => ({
            ...prev,
            [factName]: value
        }));
    };

    const handleValidate = async () => {
        if (Object.keys(factValues).length === 0) {
            alert('Please provide at least one fact value for validation');
            return;
        }

        setIsValidating(true);

        // Convert values to proper types based on fact definitions
        const typedFactValues = {};
        
        for (const factName in factValues) {
            const fact = facts.find(f => f.factName === factName);
            if (!fact) continue;
            
            let value = factValues[factName];
            
            if (value === '' || value === undefined) continue;
            
            switch(fact.type) {
                case 'number':
                    value = Number(value);
                    break;
                case 'boolean':
                    value = value.toLowerCase() === 'true';
                    break;
                case 'array':
                    try {
                        if (typeof value === 'string' && (value.startsWith('[') && value.endsWith(']'))) {
                            value = JSON.parse(value);
                        } else {
                            value = value.split(',').map(item => item.trim());
                        }
                    } catch(e) {
                        console.error('Error parsing array:', e);
                        value = [];
                    }
                    break;
                case 'object':
                    try {
                        if (typeof value === 'string') {
                            value = JSON.parse(value);
                        }
                    } catch(e) {
                        alert(`Invalid JSON for ${factName}. Please use valid JSON format.`);
                        setIsValidating(false);
                        return;
                    }
                    break;
            }
            
            typedFactValues[factName] = value;
        }
        
        try {
            await dispatch(validateRules(typedFactValues));
        } catch (error) {
            console.error('Validation failed:', error);
        } finally {
            setIsValidating(false);
        }
    };

    const renderValidationResults = () => {
        if (validationError) {
            return (
                <div className="alert alert-danger mt-4">
                    <strong>Error:</strong> {validationError}
                </div>
            );
        }
        
        if (!validationResults) {
            return null;
        }

        const { events, success, failure } = validationResults;
        const allEvents = [...(success || []), ...(failure || [])];
        
        if (allEvents.length === 0) {
            return (
                <div className="alert alert-info" style={{marginTop:'20px'}}>
                    No rules matched with the provided facts.
                </div>
            );
        }
        
        return (
            <div className="validation-results mt-4">
                <h5>Validation Results:</h5>
                
                {success && success.length > 0 && (
                    <div className="alert alert-success mb-3">
                        <h6>Successful Rules:</h6>
                        <ul className="mb-0">
                            {success.map((result, index) => (
                                <li key={`success-${index}`}>
                                    Decision Outcome: <strong>{result.params.outcome}</strong>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {failure && failure.length > 0 && (
                    <div className="alert alert-warning">
                        <h6>Failed Rules:</h6>
                        <ul className="mb-0">
                            {failure.map((result, index) => (
                                <li key={`fail-${index}`}>
                                    Decision Outcome (Not Applied): <strong>{result.params.outcome}</strong>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {/* {validationResults.ruleResults && validationResults.ruleResults.length > 0 && (
                    <div className="mt-3">
                        <details>
                            <summary className="text-primary cursor-pointer">Show Rule Evaluation Details</summary>
                            <pre className="bg-light p-3 mt-2" style={{ fontSize: '0.8rem', maxHeight: '200px', overflow: 'auto' }}>
                                {validationResults.ruleResults.map((rule, i) => {
                                    try {
                                        return JSON.stringify(JSON.parse(rule), null, 2);
                                    } catch (e) {
                                        return rule;
                                    }
                                }).join('\n\n')}
                            </pre>
                        </details>
                    </div>
                )} */}
            </div>
        );
    };

    return (
        <>
            {(!currentRule?.decisions || currentRule.decisions.length === 0) ? (
                <NoneAddedPopup 
                    heading={"No Decisions"} 
                    text={"There are no decisions available in the selected ruleset to validate."} 
                    buttonText={"DontShow"} 
                />
            ) : (
                <div className='AddBox'>
                    <h5 style={{fontSize:'20px'}}>Validate Rules</h5>
                    <p style={{fontSize:'16px'}}>Enter fact values to test the rules:</p>
                    
                    {facts.length > 0 ? (
                        <>
                            <div className="row">
                                {facts.map(fact => (
                                    <div className="col-sm-6 col-xs-12 mb-3" key={fact.id}>
                                        <label htmlFor={`fact-${fact.id}`}>{fact.factName}</label>
                                        <div className="input-group" style={{display:'flex'}}>
                                            <div className="input-group-prepend" style={{background:'#e9ecef',border:'1px solid #ced4da',borderRadius:'6px',padding:'10px 15px',borderTopRightRadius:'0px',borderBottomRightRadius:'0px'}}>
                                                <span className="input-group-text" style={{fontSize:'16px'}}>{fact.type}</span>
                                            </div>
                                            <input 
                                                id={`fact-${fact.id}`}
                                                className='form-control' 
                                                type='text' 
                                                placeholder={`Enter ${fact.type} value`}
                                                onChange={(e) => handleFactValueChange(fact.factName, e.target.value)} 
                                            />
                                        </div>
                                        {fact.type === 'object' && (
                                            <small className="text-muted">Enter valid JSON format</small>
                                        )}
                                    </div>
                                ))}
                            </div>
                            
                            <div style={{ marginTop: '30px' }}>
                                <button 
                                    className='addbtn' 
                                    style={{ padding: '12px 35px', marginRight: '15px' }}
                                    onClick={handleValidate}
                                    disabled={isValidating}
                                >
                                    {isValidating ? 'Validating...' : 'Validate'}
                                </button>
                            </div>
                            
                            {renderValidationResults()}
                        </>
                    ) : (
                        <div className="alert alert-warning">
                            No facts available. Please add facts before validating rules.
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
